import { Box, Link, Tooltip } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import useDelete from "../../../../services/hooks/useDelete";
import useGet from "../../../../services/hooks/useGet";
import usePost from "../../../../services/hooks/usePost";
import usePut from "../../../../services/hooks/usePut";
import { IMasterInvestigation } from "../../../../services/interfaces/DataInterfaces";
import restApiService from "../../../../services/restApiService";
import {
  apiEndpoints,
  notificationActions,
  notificationMessages,
} from "../../../../shared/constants/api-constant";
import { ActionButtons, Heading } from "../../../../shared/ReusableComponents";
import FileViewer from "../../../../shared/ReusableComponents/FileViewer";
import { GridTable } from "../../../../shared/ReusableComponents/GridTable";
import IconButton from "../../../../shared/ReusableComponents/IconButton";
import { CommonFunctions } from "../../../../shared/utils/CommonFunctions";
import { hideLoader, showLoader } from "../../../../store/currentPreferencesSlice";
import { UploadFiles } from "../UploadFiles/UploadFiles";
import { Constants } from "../../../../shared/constants/all-constant";
import ConfirmDialog from "../../../../shared/ReusableComponents/ConfirmDialog";
import { ICONS } from "../../../../shared/constants/enums";
interface IDocument {
  name: string;
  content: string;
  type: "image" | "pdf";
}

export const AdminInvestigation = ({ templateId }: { templateId: number }) => {
  const [investigationData, setInvestigationData] = useState<
    IMasterInvestigation[]
  >([]);
  const [openUploadDailog, setOpenUploadDailog] = useState(false);
  const [openDailog, setOpenDailog] = useState(false);
  const [selectedFileDelete, setSelectedFileDelete] =
    useState<IMasterInvestigation | null>(null);
  const [fileToEdit, setFileToEdit] = useState<any | null>(null);
  const { postService } = usePost();
  const { putService } = usePut();
  const { data, getService, loading, error } = useGet();
  const [isModelOpen, setIsModelOpen] = useState<boolean>(false);
  const [file, setFile] = useState<IDocument | undefined>(undefined);
  const { deleteService, loading: deleteLoading } = useDelete();

  const dispatch = useDispatch();

  const downLoadAndViewFile = async (fileUrl: string, fileName: string) => {
    setIsModelOpen(false);
    setFile(undefined);
    dispatch(showLoader());
    await restApiService.get(fileUrl).then(async (response) => {
      const fileData = await response.data.fileData;
      setFile({
        content: fileData,
        name: fileName,
        type: fileName.endsWith(".pdf") ? "pdf" : "image",
      });
      dispatch(hideLoader());
      if (fileData) setIsModelOpen(true);
    });
  };

  const renderFile = (params: any) => {
    const fileURL = `${apiEndpoints.TEMPLATEINVESTIGATION}/${params.row.id}`;
    return (
      <Link
        underline="always"
        sx={{ cursor: "pointer" }}
        onClick={() => downLoadAndViewFile(fileURL, params.row.fileName)}
      >
        <Tooltip title={params.row.fileName}>{params.row.fileName}</Tooltip>
      </Link>
    );
  };
  const onModelClose = () => {
    setIsModelOpen(false);
  };

  const columns: GridColDef[] = [
    {
      field: "fileName",
      headerName: "File Name",
      renderCell: renderFile,
    },
    {
      field: "offset",
      headerName: "Offset",
      valueGetter: (value: any, row) =>
        value === 0
          ? Constants.DEFAULT_OFFSET_VALUE
          : CommonFunctions.convertMinutesToOffset(value),
    },
    {
      field: "",
      headerName: "Uploaded By",
      resizable: false,
      valueGetter: (value: any, row) => {
        return CommonFunctions.formatFirstAndLastName(
          row.firstName,
          row.lastName,
          { prefixKey: "doctor" }
        );
      },
    },
    ...(CommonFunctions.IsCurrentSuperUser()
      ? [
          {
            field: "action",
            width: 120,
            resizable: false,
            headerName: "Action",
            renderCell: (params: any) => (
              <ActionButtons
                onEdit={() => handleEdit(params.row)}
                onDelete={() => handleDelete(params.row)}
              />
            ),
          },
        ]
      : []),
  ];
  const handleDelete = (data: any) => {
    setSelectedFileDelete(data);
    setOpenDailog(true);
  };
  const handleEdit = (data: any) => {
    setFileToEdit(data);
    setOpenUploadDailog(true);
  };

  const handleFileUpload = async (
    file: File | null | any,
    isEdit: boolean,
    offset: number
  ) => {
    dispatch(showLoader());
    let payload = {} as any;

    if (file && file?.type) {
      payload = {
        fileName: file.name,
        fileType: file.type,
        fileData: await CommonFunctions.convertFileToBase64(file),
        offset: offset,
      };
    } else {
      payload = {
        fileName: fileToEdit.fileName,
        fileType: fileToEdit.fileType,
        fileData: file?.fileData,
        offset: offset,
      };
    }

    const fileName = payload.fileName;
    const truncateFileName = CommonFunctions.truncateText(fileName, 25);
    const successMessage = `${truncateFileName} ${notificationMessages.FILE} ${
      isEdit ? notificationActions.UPDATED : notificationActions.UPLOADED
    }.`;

    if (isEdit) {
      await putService(
        `${apiEndpoints.TEMPLATEINVESTIGATION}`,
        { id: fileToEdit.id, ...payload },
        successMessage
      );
    } else {
      await postService(
        apiEndpoints.TEMPLATEINVESTIGATION,
        { templateId, ...payload },
        successMessage
      );
    }
    getService(`${apiEndpoints.MASTERTEMPLATEINVESTIGATIONS}/${templateId}`);
    setOpenUploadDailog(false);
    setFileToEdit(null);
    dispatch(hideLoader());
  };

  const confirmDelete = () => {
    const message = `${CommonFunctions.truncateText(
      selectedFileDelete?.fileName ? selectedFileDelete?.fileName : "",
      25
    )} ${notificationMessages.FILE} ${notificationActions.DELETED}.`;

    if (selectedFileDelete) {
      setInvestigationData((prevData) =>
        prevData.filter((file) => file.id !== selectedFileDelete.id)
      );
      deleteService(
        `${apiEndpoints.TEMPLATEINVESTIGATION}/${selectedFileDelete.id}`,
        null,
        message
      );
    }
    setOpenDailog(false);
    dispatch(hideLoader());
  };

  useEffect(() => {
    if (error) return;
    if (!(data && investigationData)) {
      getService(`${apiEndpoints.MASTERTEMPLATEINVESTIGATIONS}/${templateId}`);
    }
  }, [openDailog]);

  useEffect(() => {
    data && setInvestigationData(data);
  }, [data]);

  useEffect(() => {
    loading && dispatch(showLoader());
    !loading && dispatch(hideLoader());
  }, [loading]);

  useEffect(() => {
    deleteLoading && dispatch(showLoader());
    !deleteLoading && dispatch(hideLoader());
  }, [deleteLoading]);

  return (
    <div style={{ backgroundColor: "#fff", padding: 0 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginLeft: "18px",
        }}
      >
        <div style={{ fontSize: "20px" }}>
          <span style={{ fontWeight: 700 }}>Attached Files</span>
          {CommonFunctions.IsCurrentSuperUser() && (
            <IconButton
              sx={{
                ml: "10px",
                p: "5px 10px",
                textTransform: "capitalize",
                fontSize: "16px",
                columnGap: "8px",
                fontWeight: 700,
              }}
              text="Upload File"
              icon={
                <span className="material-symbols-outlined">upload_file</span>
              }
              onClick={() => setOpenUploadDailog(true)}
            />
          )}
        </div>
      </div>
      <Box sx={{ width: "750px" }}>
        <GridTable
          rows={investigationData}
          columns={columns}
          pageSizeOptions={[5, 10, 15]}
          initialPageSize={10}
        />
      </Box>
      <UploadFiles
        open={openUploadDailog}
        onClose={() => {
          setOpenUploadDailog(false);
          setFileToEdit(null);
        }}
        onUpload={handleFileUpload}
        fileToEdit={fileToEdit}
      />

      <ConfirmDialog
        dialogOpen={openDailog}
        width="xs"
        setDialogOpen={() => setOpenDailog(false)}
        handleConfirm={confirmDelete}
        confirmText="Delete"
        cancelText="Cancel"
        icon={ICONS.DELETE}
        className="delete-icon"
        text={
          <>
            <Heading
              text={`${notificationMessages.DELETE_RECORD_CONFIRMATION} ${notificationActions.FILE}?`}
              isBold
              font="1.25rem"
            />
            <span
              className="medName-caption"
              title={selectedFileDelete?.fileName}
            >
              “
              {CommonFunctions.truncateText(
                selectedFileDelete?.fileName
                  ? selectedFileDelete?.fileName
                  : "",
                25
              )}
              ”
            </span>
          </>
        }
      />
      <FileViewer
        file={file}
        isModelOpen={isModelOpen}
        onModelClose={onModelClose}
        setIsModelOpen={setIsModelOpen}
      />
    </div>
  );
};
